import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import AffiliateApplicationsTable from "../../../components/Affiliates/AffiliateApplicationsTable";
import Navbar from "../../../components/Navbar";
import PageTransition from "../../../components/PageTransition";

const AffiliateApplications = () => {
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  const handleStatsUpdate = (newStats) => {
    setStats(newStats);
    setIsLoading(false);
  };

  return (
    <>
      <Navbar page="Affiliate Applications" />
      <Helmet>
        <title>Affiliate Applications - DealBoxx Vendor</title>
        <meta
          name="description"
          content="Manage affiliate applications for deal promotions"
        />
      </Helmet>

      <PageTransition className="min-h-screen bg-gray-50">
        <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                  Affiliate Applications
                </h1>
                <p className="mt-2 text-base lg:text-lg text-gray-600">
                  Review and manage affiliate applications for your deals
                </p>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:flex lg:space-x-4 gap-4">
                <motion.div 
                  className="p-3 lg:p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.div 
                    className="text-xl lg:text-2xl font-bold text-blue-600"
                    key={stats.total}
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {isLoading ? (
                      <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      stats.total
                    )}
                  </motion.div>
                  <div className="text-xs lg:text-sm text-gray-600">
                    Total Applications
                  </div>
                </motion.div>
                <motion.div 
                  className="p-3 lg:p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.div 
                    className="text-xl lg:text-2xl font-bold text-yellow-600"
                    key={stats.pending}
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {isLoading ? (
                      <div className="w-8 h-8 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      stats.pending
                    )}
                  </motion.div>
                  <div className="text-xs lg:text-sm text-gray-600">Pending</div>
                </motion.div>
                <motion.div 
                  className="p-3 lg:p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.div 
                    className="text-xl lg:text-2xl font-bold text-green-600"
                    key={stats.approved}
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {isLoading ? (
                      <div className="w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      stats.approved
                    )}
                  </motion.div>
                  <div className="text-xs lg:text-sm text-gray-600">Approved</div>
                </motion.div>
                <motion.div 
                  className="p-3 lg:p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.div 
                    className="text-xl lg:text-2xl font-bold text-red-600"
                    key={stats.rejected}
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {isLoading ? (
                      <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      stats.rejected
                    )}
                  </motion.div>
                  <div className="text-xs lg:text-sm text-gray-600">Rejected</div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-6">
            {/* Applications Table */}
            <AffiliateApplicationsTable onStatsUpdate={handleStatsUpdate} />
          </div>

          {/* Help Section */}
          <div className="p-4 lg:p-6 mt-8 bg-blue-50 rounded-xl">
            <div className="flex flex-col sm:flex-row items-start">
              <div className="flex-shrink-0 mb-3 sm:mb-0">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="sm:ml-3 flex-1">
                <h3 className="text-base lg:text-lg font-medium text-blue-900">
                  Need Help with Affiliate Applications?
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    Here are some tips for managing affiliate applications
                    effectively:
                  </p>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    <li>
                      Review each application carefully before making a decision
                    </li>
                    <li>
                      Check the affiliate's social media reach and engagement
                    </li>
                    <li>
                      Consider the brand alignment and target audience match
                    </li>
                    <li>Provide clear feedback when rejecting applications</li>
                    <li>Monitor approved affiliates' performance regularly</li>
                  </ul>
                </div>
                <div className="mt-4">
                  <button className="text-sm font-medium text-blue-600 hover:text-blue-500">
                    Learn more about affiliate management →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageTransition>
    </>
  );
};

export default AffiliateApplications;
